import {
  AuthUser,
  confirmResetPassword,
  confirmSignUp as amplifyConfirmSignUp,
  deleteUser as amplifyDeleteUser,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  updatePassword,
} from 'aws-amplify/auth'
import { useCallback, useContext, useEffect, useState } from 'react'

import { HelperFuncs } from '../../utilities'
import {
  ChangePasswordInput,
  ConfirmSignUpInput,
  ForgotPasswordInput,
  ResendSignUpInput,
  ResetPasswordInput,
  SignInInput,
  SignUpInput,
} from './types'
import { UserContext } from './User.provider'

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null)

  useEffect(() => {
    let active = true
    const check = async () => {
      try {
        const newUser = await getCurrentUser()
        if (active) setUser(newUser)
      } catch (error) {
        if (active) setUser(null)
      }
    }
    check()
    return () => {
      active = false
    }
  }, [setUser])

  const signIn = useCallback(
    async ({ email, password }: SignInInput) => {
      try {
        await amplifySignOut()
      } catch {
        // ignore if no one was signed in
      }
      await amplifySignIn({ username: email, password })
      setUser(await getCurrentUser())
    },
    [setUser]
  )

  const signOut = useCallback(async () => {
    await amplifySignOut()
    setUser(null)
  }, [setUser])

  const deleteUser = useCallback(async () => {
    await amplifyDeleteUser()
    setUser(null)
  }, [setUser])

  return { user, signIn, signOut, deleteUser }
}

export const useSignOut = () => {
  return useContext(UserContext).signOut
}

export const useSignUpFunctions = () => ({
  signUp: async function signUp({ email }: SignUpInput): Promise<string> {
    const password = HelperFuncs.generateRandomPassword(12)
    await amplifySignUp({
      username: email.toLowerCase(),
      password,
      options: {
        userAttributes: { email: email.toLowerCase() },
      },
    })
    return password
  },

  confirmSignUp: async function confirmSignUp({
    email,
    code,
    password,
  }: ConfirmSignUpInput) {
    await amplifyConfirmSignUp({ username: email, confirmationCode: code })
    await amplifySignIn({ username: email, password })
  },

  resendSignUp: async function resendSignUp({ email }: ResendSignUpInput) {
    await resendSignUpCode({ username: email })
  },
})

export const usePasswordFunctions = () => ({
  forgotPassword: async function forgotPassword({
    email,
  }: ForgotPasswordInput) {
    await resetPassword({ username: email })
  },

  resetPassword: async function resetPasswordFn({
    email,
    code,
    password,
  }: ResetPasswordInput) {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword: password,
    })
  },

  changePassword: async function ({
    password,
    newPassword,
  }: ChangePasswordInput) {
    await updatePassword({ oldPassword: password, newPassword: newPassword })
  },
})
